import { useMemo, useState } from 'react';
import { FAQ, FAQ_PERSONA_INTRO, FAQ_PERSONA_LABEL, faqFor, faqFromRows, faqSearch, type FaqItem, type FaqPersona } from '@antreja/ui/faq';
import { useFaqItems } from '../../data/hooks';
import { useClub } from '../../app/club';
import { PageHeader, Panel } from '../../components/ui';
import type { PortalRole } from '../../lib/types';

const PERSONAS: readonly FaqPersona[] = ['clubbeheerder', 'groepsbeheerder', 'installateur', 'lid'];

/** Eerste tab: de vragen die bij je rol in deze club passen. */
export function defaultPersona(role: PortalRole | null): FaqPersona {
  if (role === 'group_manager') return 'groepsbeheerder';
  if (role === 'installer') return 'installateur';
  return 'clubbeheerder';
}

function Answer({ item }: { item: FaqItem }) {
  const out: JSX.Element[] = [];
  let bullets: string[] = [];
  const flush = () => {
    if (bullets.length)
      out.push(
        <ul key={`ul-${out.length}`}>
          {bullets.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>,
      );
    bullets = [];
  };
  for (const line of item.a) {
    if (line.startsWith('• ')) bullets.push(line.slice(2));
    else {
      flush();
      out.push(<p key={`p-${out.length}`}>{line}</p>);
    }
  }
  flush();
  return <div className="faq-answer">{out}</div>;
}

/** Hulp: veelgestelde vragen per persona (dezelfde bron als de app), met zoeken. */
export function HelpPage() {
  const { role } = useClub();
  const [persona, setPersona] = useState<FaqPersona>(defaultPersona(role));
  const [query, setQuery] = useState('');
  const searching = query.trim().length > 1;
  const faq = useFaqItems();
  // Actuele teksten uit admin.antreja.nl; tijdens laden of bij een fout de ingebouwde teksten.
  const source = useMemo(() => (faq.data?.length ? faqFromRows(faq.data) : FAQ), [faq.data]);
  const items = useMemo(() => {
    // Portaal én app: een beheerder helpt ook leden en installateurs met de app.
    const all = (ps: readonly FaqPersona[]) => {
      const seen = new Set<string>();
      return [...faqFor('portaal', ps, source), ...faqFor('app', ps, source)].filter((f) => (seen.has(f.id) ? false : (seen.add(f.id), true)));
    };
    return searching ? faqSearch(all(PERSONAS), query) : all([persona]);
  }, [persona, query, searching, source]);

  return (
    <div className="stack-lg">
      <PageHeader
        title="Hulp"
        intro="Veelgestelde vragen over het beheerportaal en de Antreja-app. Staat je vraag er niet bij? Vraag het aan je clubbeheerder of neem contact op met Antreja."
      />
      <Panel>
        <div className="toolbar">
          <div className="tabs" role="tablist" aria-label="Voor wie" style={{ margin: 0, border: 0 }}>
            {PERSONAS.map((p) => (
              <button
                key={p}
                role="tab"
                aria-selected={!searching && p === persona}
                onClick={() => {
                  setPersona(p);
                  setQuery('');
                }}
              >
                {FAQ_PERSONA_LABEL[p]}
              </button>
            ))}
          </div>
          <input
            className="input"
            type="search"
            placeholder="Zoeken in alle vragen"
            aria-label="Zoeken in alle vragen"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ maxWidth: 260 }}
          />
        </div>
        <div className="panel-pad">
          {!searching && <p className="muted small">{FAQ_PERSONA_INTRO[persona]}</p>}
          {items.length === 0 ? (
            <p className="tile-empty">Geen vragen gevonden. Probeer een ander woord.</p>
          ) : (
            <div className="faq-list">
              {items.map((f) => (
                <details key={f.id} className="faq-item">
                  <summary>
                    {f.q}
                    {searching && <span className="faq-tag">{FAQ_PERSONA_LABEL[f.persona]}</span>}
                  </summary>
                  <Answer item={f} />
                </details>
              ))}
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}
